// ============================================================================
// Delete Tranches Modal Component
// Allows selecting and deleting specific tranches from a position
// ============================================================================

import React, { useState, useMemo } from 'react';
import { Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import type { Tranche } from '../types';
import { TransactionType } from '../types';
import { formatCurrency } from '../utils/formatters';

interface DeleteTranchesModalProps {
  isOpen: boolean;
  onClose: () => void;
  tranches: Tranche[];
  onConfirmDelete: (trancheIds: string[]) => Promise<void>;
  stockName: string;
}

const DeleteTranchesModal: React.FC<DeleteTranchesModalProps> = ({
  isOpen,
  onClose,
  tranches,
  onConfirmDelete,
  stockName,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sort tranches by date (oldest first)
  const sortedTranches = useMemo(() => {
    return [...tranches].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [tranches]);

  const allSelected = selectedIds.size === tranches.length;
  const someSelected = selectedIds.size > 0;

  const handleToggle = (trancheId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(trancheId)) {
      newSelected.delete(trancheId);
    } else {
      newSelected.add(trancheId);
    }
    setSelectedIds(newSelected);
    setError(null);
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(tranches.map(t => t.id)));
    }
    setError(null);
  };

  const handleConfirm = async () => {
    if (selectedIds.size === 0) {
      setError('Please select at least one tranche to delete');
      return;
    }

    if (selectedIds.size === tranches.length) {
      setError('Cannot delete all tranches. To remove all tranches, delete the entire position instead.');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await onConfirmDelete(Array.from(selectedIds));
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tranches');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setError(null);
    onClose();
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Delete Tranches - {stockName}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-gray-600 mb-4">
            Select the tranches you want to permanently delete. This action cannot be undone.
          </p>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Select All Header */}
          <div className="flex items-center gap-3 py-2 px-3 bg-gray-50 rounded-t-lg border border-gray-200">
            <Checkbox
              checked={allSelected}
              onCheckedChange={handleSelectAll}
              id="select-all"
            />
            <label htmlFor="select-all" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
              Select All ({tranches.length} tranches)
            </label>
          </div>

          {/* Tranche List */}
          <div className="border border-t-0 border-gray-200 rounded-b-lg divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {sortedTranches.map((tranche, index) => {
              const isBuy = tranche.type === TransactionType.BUY;
              const isSelected = selectedIds.has(tranche.id);

              return (
                <div
                  key={tranche.id}
                  className={`flex items-center gap-4 p-3 hover:bg-gray-50 cursor-pointer ${
                    isSelected ? 'bg-red-50' : ''
                  }`}
                  onClick={() => handleToggle(tranche.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggle(tranche.id)}
                  />
                  
                  {/* Type Badge */}
                  <span
                    className={`px-2 py-0.5 text-xs font-semibold rounded ${
                      isBuy
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {isBuy ? 'BUY' : 'SELL'}
                  </span>

                  {/* Tranche Number */}
                  <span className="text-sm font-medium text-gray-600 w-8">
                    T{index + 1}
                  </span>

                  {/* Date */}
                  <span className="text-sm text-gray-700 w-28">
                    {formatDate(tranche.timestamp)}
                  </span>

                  {/* Quantity */}
                  <span className="text-sm text-gray-900 w-20">
                    {tranche.quantity} qty
                  </span>

                  {/* Price */}
                  <span className="text-sm text-gray-900 flex-1">
                    @ {formatCurrency(tranche.price)}
                  </span>

                  {/* Total Value */}
                  <span className="text-sm font-medium text-gray-700">
                    {formatCurrency(tranche.quantity * tranche.price)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Selection Summary */}
          {someSelected && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>{selectedIds.size}</strong> tranche{selectedIds.size !== 1 ? 's' : ''} selected for deletion
                {selectedIds.size === tranches.length && (
                  <span className="block mt-1 text-red-600 font-medium">
                    ⚠️ You cannot delete all tranches. At least one must remain.
                  </span>
                )}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isDeleting || selectedIds.size === 0 || selectedIds.size === tranches.length}
            className="gap-2"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteTranchesModal;
